import { cn } from "@-/com.npmjs.cn";
import { View } from "@-/com.npmjs.react-native";
import type { AccessibilityActionEvent, LayoutChangeEvent } from "@-/com.npmjs.react-native";
import { Gesture, GestureDetector } from "@-/com.npmjs.react-native-gesture-handler";
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from "@-/com.npmjs.react-native-reanimated";
import useLatest from "@-/com.npmjs.use-latest";
import { useEffect, useMemo } from "react";

const THUMB_SIZE_PX = 24;
/** Horizontal travel before the drag wins over taps and vertical scrolling underneath. */
const HORIZONTAL_ACTIVATION_PX = 4;
/** Vertical travel that fails the drag, leaving it to an enclosing scroll view. */
const VERTICAL_FAIL_PX = 12;
const SNAP_SPRING = { damping: 22, mass: 0.6, stiffness: 320 } as const;

/** The thumb's left edge when it rests on stop `index` of a track `width` wide. */
function stopOffset(index: number, width: number, stopCount: number) {
  "worklet";
  const travel = Math.max(0, width - THUMB_SIZE_PX);
  return stopCount <= 1 ? 0 : (travel * index) / (stopCount - 1);
}

/** The stop nearest a touch at `x` (relative to the track). */
function stopIndexAt(x: number, width: number, stopCount: number) {
  "worklet";
  const travel = Math.max(1, width - THUMB_SIZE_PX);
  const fraction = Math.min(1, Math.max(0, (x - THUMB_SIZE_PX / 2) / travel));
  return Math.round(fraction * (stopCount - 1));
}

/**
 * A slider that can only rest on `stopCount` evenly spaced stops (like a Kindle's text size
 * control). While dragging, the thumb jumps to the nearest stop (it never tracks the finger
 * continuously); `onIndexChange` fires as soon as that stop changes so whatever it controls
 * previews live. A tap anywhere on the track jumps to the nearest stop.
 *
 * Exposed to assistive technology as an adjustable control: increment/decrement step one stop,
 * and `valueLabel` is read as its value.
 */
export function SnapSlider({
  accessibilityLabel,
  className,
  index,
  onIndexChange,
  stopCount,
  testID,
  valueLabel,
}: {
  accessibilityLabel: string;
  className?: string;
  index: number;
  onIndexChange: (index: number) => void;
  stopCount: number;
  testID?: string;
  valueLabel?: string;
}) {
  const onIndexChangeRef = useLatest(onIndexChange);
  const trackWidth = useSharedValue(0);
  const thumbOffset = useSharedValue(0);
  const isDragging = useSharedValue(false);
  const selectedIndex = useSharedValue(index);

  // Follow an index set from elsewhere (the same setting changed from a menu, a reset), except
  // mid-drag: the gesture owns the selection, and a re-render for a stop the drag already passed
  // would otherwise rewind the UI thread's selection.
  useEffect(() => {
    if (isDragging.get()) {
      return;
    }
    selectedIndex.set(index);
    thumbOffset.set(withSpring(stopOffset(index, trackWidth.get(), stopCount), SNAP_SPRING));
  }, [index, isDragging, selectedIndex, stopCount, thumbOffset, trackWidth]);

  const gesture = useMemo(() => {
    const reportIndex = (nextIndex: number) => {
      onIndexChangeRef.current(nextIndex);
    };
    const select = (nextIndex: number) => {
      "worklet";
      if (nextIndex !== selectedIndex.get()) {
        selectedIndex.set(nextIndex);
        thumbOffset.set(
          withSpring(stopOffset(nextIndex, trackWidth.get(), stopCount), SNAP_SPRING),
        );
        runOnJS(reportIndex)(nextIndex);
      }
    };

    const drag = Gesture.Pan()
      .activeOffsetX([-HORIZONTAL_ACTIVATION_PX, HORIZONTAL_ACTIVATION_PX])
      .failOffsetY([-VERTICAL_FAIL_PX, VERTICAL_FAIL_PX])
      .onStart((event) => {
        isDragging.set(true);
        select(stopIndexAt(event.x, trackWidth.get(), stopCount));
      })
      .onUpdate((event) => {
        select(stopIndexAt(event.x, trackWidth.get(), stopCount));
      })
      .onFinalize(() => {
        isDragging.set(false);
      });
    const tap = Gesture.Tap().onEnd((event, success) => {
      if (success) {
        select(stopIndexAt(event.x, trackWidth.get(), stopCount));
      }
    });
    return Gesture.Race(drag, tap);
  }, [isDragging, onIndexChangeRef, selectedIndex, stopCount, thumbOffset, trackWidth]);

  const thumbStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: thumbOffset.get() }],
  }));

  const handleLayout = (event: LayoutChangeEvent) => {
    const { width } = event.nativeEvent.layout;
    trackWidth.set(width);
    if (!isDragging.get()) {
      thumbOffset.set(stopOffset(selectedIndex.get(), width, stopCount));
    }
  };

  const handleAccessibilityAction = (event: AccessibilityActionEvent) => {
    const step = event.nativeEvent.actionName === "increment" ? 1 : -1;
    const nextIndex = Math.min(stopCount - 1, Math.max(0, index + step));
    if (nextIndex !== index) {
      onIndexChange(nextIndex);
    }
  };

  return (
    <GestureDetector gesture={gesture}>
      <View
        accessibilityActions={[{ name: "increment" }, { name: "decrement" }]}
        accessibilityLabel={accessibilityLabel}
        accessibilityRole="adjustable"
        accessibilityValue={{ max: stopCount - 1, min: 0, now: index, text: valueLabel }}
        accessible={true}
        className={cn("h-11 justify-center", className)}
        onAccessibilityAction={handleAccessibilityAction}
        onLayout={handleLayout}
        testID={testID}
      >
        {/* The track and its stops span thumb center to thumb center. */}
        <View className="absolute inset-x-3 h-full justify-center" pointerEvents="none">
          <View className="h-0.5 rounded-full bg-border" />
          {Array.from({ length: stopCount }, (_, stop) => (
            <View
              className="absolute -ml-px h-2.5 w-0.5 rounded-full bg-muted-foreground/60"
              key={stop}
              style={{ left: `${stopCount <= 1 ? 0 : (stop / (stopCount - 1)) * 100}%` }}
            />
          ))}
        </View>
        <Animated.View
          className="absolute top-2.5 left-0 rounded-full border border-border bg-background shadow-sm shadow-black/20"
          pointerEvents="none"
          style={[{ height: THUMB_SIZE_PX, width: THUMB_SIZE_PX }, thumbStyle]}
        />
      </View>
    </GestureDetector>
  );
}
